import React, { useCallback, useEffect, useState, useRef, useMemo, useLayoutEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import { useAtom } from 'jotai';
import { plugPowerHistoryAtom, BuildingIDProps } from '../atoms/chargers';
import { Box, Button, Checkbox, CircularProgress, FormControl, ListItemText, MenuItem, Select, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { calculateTimeIntervals, formatTimeLabel, shouldShowTimeLabel, toEasternDateTime } from '../converters/timespanConverter';

const PLUG_COLORS = [
  'firebrick', 'steelblue', 'seagreen', 'orange', 'slateblue',
  'purple', 'lightsalmon', 'teal', 'goldenrod', 'coral',
  'mediumpurple', 'darkcyan',
];
const CHARGING_POWER_THRESHOLD_W = 50;
const CHARGING_STATE_VALUE = 3;

type LastChargeSession = {
  startMs: number;
  endMs: number | null;
};

interface PlugPowerGraphProps extends BuildingIDProps {
  startDateTime: DateTime;
  endDateTime: DateTime;
  isLoading?: boolean;
  forceNoDataOnEmpty?: boolean;
  unavailablePlugNums?: number[];
  maxPlugsOverride?: number;
  showAvailableOnly?: boolean;
  scaleMode?: 'auto' | '10' | '1000';
}

const PlugPowerGraph: React.FC<PlugPowerGraphProps> = ({
  BuildingID,
  startDateTime,
  endDateTime,
  isLoading,
  forceNoDataOnEmpty,
  unavailablePlugNums = [],
  maxPlugsOverride = 0,
  showAvailableOnly = false,
  scaleMode = 'auto',
}) => {
  const [historyMap] = useAtom(plugPowerHistoryAtom);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 480);
  const [legendSelected, setLegendSelected] = useState<Record<string, boolean>>({});
  const chartRef = useRef<ReactECharts>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hoveredSeriesNameRef = useRef<string | null>(null);
  const [containerReady, setContainerReady] = useState(false);
  const unavailableSet = useMemo(() => new Set(unavailablePlugNums), [unavailablePlugNums]);

  const syncChartWidthToContainer = useCallback((force = false) => {
    if (!chartRef.current || !containerRef.current) return;
    const chart = chartRef.current.getEchartsInstance();
    const rect = containerRef.current.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const chartWidth = chart.getWidth();
    const chartHeight = chart.getHeight();
    if (
      force ||
      Math.abs(chartWidth - rect.width) > 2 ||
      Math.abs(chartHeight - rect.height) > 2
    ) {
      chart.resize({ width: Math.floor(rect.width), height: Math.floor(rect.height) });
    }
  }, []);

  const historyData = useMemo(
    () => (
      BuildingID && Array.isArray(historyMap[BuildingID.toLowerCase()])
        ? historyMap[BuildingID.toLowerCase()]
        : []
    ),
    [BuildingID, historyMap]
  );

  const maxPlugsFromHistory =
    historyData.length > 0 ? Math.max(...historyData.map((r) => (r.CPower?.length ?? 0))) : 0;
  const maxPlugs = Math.max(maxPlugsFromHistory, maxPlugsOverride);
  const visiblePlugNums = Array.from({ length: maxPlugs }, (_, i) => i + 1).filter((plugNum) => {
    if (!showAvailableOnly) return true;
    return !unavailableSet.has(plugNum);
  });
  const selectedPlugNums = useMemo(
    () => visiblePlugNums.filter((plugNum) => legendSelected[`Plug ${plugNum}`] !== false),
    [visiblePlugNums, legendSelected]
  );
  const lastChargeOrder = useMemo(() => {
    const latestByPlug = new Map<number, LastChargeSession>();
    const activeStartByPlug = new Map<number, number>();
    for (const row of historyData) {
      const ts = toEasternDateTime(row).toMillis();
      for (let plugNum = 1; plugNum <= maxPlugs; plugNum++) {
        const state = Number(row.CState?.[plugNum - 1]);
        const raw = row.CPower?.[plugNum - 1];
        const watts = Number(raw);
        const isChargingByState = Number.isFinite(state) && state === CHARGING_STATE_VALUE;
        const isChargingByPower = Number.isFinite(watts) && watts > CHARGING_POWER_THRESHOLD_W;
        const isCharging = isChargingByState || (!Number.isFinite(state) && isChargingByPower);
        if (isCharging) {
          if (!activeStartByPlug.has(plugNum)) activeStartByPlug.set(plugNum, ts);
          continue;
        }
        const startMs = activeStartByPlug.get(plugNum);
        if (startMs != null) {
          latestByPlug.set(plugNum, { startMs, endMs: ts });
          activeStartByPlug.delete(plugNum);
        }
      }
    }
    activeStartByPlug.forEach((startMs, plugNum) => {
      latestByPlug.set(plugNum, { startMs, endMs: null });
    });
    return visiblePlugNums
      .map((plugNum) => ({ plugNum, session: latestByPlug.get(plugNum) }))
      .filter((item): item is { plugNum: number; session: LastChargeSession } => !!item.session)
      .sort((a, b) => a.session.startMs - b.session.startMs)
      .map((item) => item.plugNum);
  }, [historyData, maxPlugs, visiblePlugNums]);
  const hidePlug = useCallback((plugNum: number) => {
    const key = `Plug ${plugNum}`;
    setLegendSelected((prev) => ({ ...prev, [key]: false }));
  }, []);

  useEffect(() => {
    const next: Record<string, boolean> = {};
    for (const plugNum of visiblePlugNums) {
      const key = `Plug ${plugNum}`;
      next[key] = legendSelected[key] ?? true;
    }
    const sameSize = Object.keys(next).length === Object.keys(legendSelected).length;
    const sameValues = sameSize && Object.keys(next).every((k) => legendSelected[k] === next[k]);
    if (!sameValues) setLegendSelected(next);
  }, [visiblePlugNums, legendSelected]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 480);
      syncChartWidthToContainer(true);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [syncChartWidthToContainer]);

  useLayoutEffect(() => {
    if (containerRef.current) {
      const { width, height } = containerRef.current.getBoundingClientRect();
      if (width > 0 && height > 0) {
        setContainerReady(true);
      } else {
        const timer = setTimeout(() => {
          if (containerRef.current) {
            const { width: w, height: h } = containerRef.current.getBoundingClientRect();
            if (w > 0 && h > 0) setContainerReady(true);
          }
        }, 0);
        return () => clearTimeout(timer);
      }
    }
  }, [historyData.length, BuildingID]);

  useEffect(() => {
    if (!containerRef.current || !containerReady) return;
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0 && chartRef.current) syncChartWidthToContainer(true);
      }
    });
    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, [containerReady, syncChartWidthToContainer]);

  useEffect(() => {
    if (historyData.length > 0 && chartRef.current && containerReady) {
      const timer = setTimeout(() => syncChartWidthToContainer(true), 100);
      return () => clearTimeout(timer);
    }
  }, [historyData, containerReady, syncChartWidthToContainer]);

  useEffect(() => {
    if (!containerReady) return;
    const timers = [0, 50, 100, 200, 400, 800, 1200, 1800, 2500].map((ms) =>
      setTimeout(() => syncChartWidthToContainer(true), ms)
    );
    return () => timers.forEach((t) => clearTimeout(t));
  }, [containerReady, BuildingID, historyData.length, syncChartWidthToContainer]);

  if (!BuildingID) return null;

  if (!historyData || historyData.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '300px', textAlign: 'center' }}>
        {isLoading ? (
          <>
            <CircularProgress style={{ color: 'firebrick' }} />
            <h3 style={{ color: 'grey', marginTop: '20px' }}>Loading plug power history…</h3>
          </>
        ) : (
          <h3 style={{ color: 'grey' }}>No plug power data for selected range</h3>
        )}
      </div>
    );
  }

  if (maxPlugs === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '300px', textAlign: 'center' }}>
        <h3 style={{ color: 'grey' }}>No plug power values in selected data</h3>
      </div>
    );
  }
  if (showAvailableOnly && visiblePlugNums.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '300px', textAlign: 'center' }}>
        <h3 style={{ color: 'grey' }}>No available plugs for this BuildingID</h3>
      </div>
    );
  }

  const xAxisData: string[] = [];
  const verticalLines: { xAxis: number; lineStyle: object; label: object; tooltip: object }[] = [];
  let previousDate = '';
  const { interval, showHalfHours } = calculateTimeIntervals(startDateTime, endDateTime);

  const thinned = historyData.length > 120
    ? historyData.filter((r, i) => {
        const m = toEasternDateTime(r);
        const prev = i > 0 ? toEasternDateTime(historyData[i - 1]) : null;
        return !prev || m.minute !== prev.minute || m.hour !== prev.hour || m.day !== prev.day;
      })
    : historyData;
  const displayData = thinned;

  displayData.forEach((r, index) => {
    const timestamp = toEasternDateTime(r);
    const formattedTime = formatTimeLabel(timestamp, showHalfHours);
    const currentDate = timestamp.toFormat('M/d');
    xAxisData.push(formattedTime);
    if (currentDate !== previousDate && previousDate !== '') {
      verticalLines.push({
        xAxis: index,
        lineStyle: { color: 'lightgrey', type: 'dashed' },
        label: { formatter: currentDate, position: 'end', fontSize: 10, color: 'grey' },
        tooltip: { formatter: `Date: ${currentDate}` },
      });
    }
    previousDate = currentDate;
  });

  const seriesData = [];
  for (let seriesIdx = 0; seriesIdx < visiblePlugNums.length; seriesIdx++) {
    const plugNum = visiblePlugNums[seriesIdx];
    const plugIdx = plugNum - 1;
    const data = displayData.map((r) => {
      const arr = r.CPower ?? [];
      if (plugIdx >= arr.length) return null;
      const raw = arr[plugIdx];
      if (raw === null || raw === undefined) return null;
      const n = Number(raw);
      return Number.isNaN(n) ? null : n;
    });
    const isUnavailable = unavailableSet.has(plugNum);
    const color = isUnavailable ? '#9ca3af' : PLUG_COLORS[plugIdx % PLUG_COLORS.length];
    seriesData.push({
      name: `Plug ${plugNum}`,
      type: 'line',
      data,
      lineStyle: { color, width: isUnavailable ? 1.5 : 2, type: isUnavailable ? 'dashed' : 'solid' },
      itemStyle: { color, opacity: isUnavailable ? 0.7 : 1 },
      emphasis: { disabled: isUnavailable },
      markLine: seriesIdx === 0 ? { symbol: 'none', data: verticalLines } : undefined,
    });
  }

  const legendNames = seriesData.map((s) => s.name);
  const allVisiblePowers = displayData.flatMap((row) =>
    visiblePlugNums
      .map((plugNum) => {
        const raw = row.CPower?.[plugNum - 1];
        if (raw === null || raw === undefined) return null;
        const n = Number(raw);
        return Number.isFinite(n) ? n : null;
      })
      .filter((v): v is number => v != null)
  );
  const maxObservedPower = allVisiblePowers.length > 0 ? Math.max(0, ...allVisiblePowers) : 0;
  const autoScaleMax = maxObservedPower <= 10 ? 10 : Math.ceil((maxObservedPower * 1.15) / 10) * 10;
  const yAxisMax = scaleMode === '1000' ? 1000 : scaleMode === '10' ? 10 : autoScaleMax;

  const option = {
    backgroundColor: 'transparent',
    animation: true,
    animationDuration: 300,
    animationEasing: 'cubicOut' as const,
    title: { text: 'Plug Power Over Time', textStyle: { color: 'grey' } },
    tooltip: {
      show: !isMobile,
      trigger: 'axis' as const,
      triggerOn: 'mousemove|click',
      axisPointer: { type: 'line' as const, snap: true },
      formatter: (params: Array<{ dataIndex: number; seriesName: string; value: unknown; color: string }> | { dataIndex: number; seriesName: string; value: unknown; color: string }) => {
        const list = Array.isArray(params) ? params : [params];
        const preferred = hoveredSeriesNameRef.current;
        const chosen = (preferred && list.find((p) => p.seriesName === preferred))
          || list.find((p) => p.value != null && p.value !== '-')
          || list[0];
        if (!chosen) return '';
        const idx = chosen.dataIndex ?? 0;
        const row = displayData[idx];
        const timestamp = row ? toEasternDateTime(row).toFormat('MM/dd/yyyy h:mm:ss a') : '';
        const plugMatch = chosen.seriesName.match(/^Plug\s+(\d+)$/);
        const plugNum = plugMatch ? Number(plugMatch[1]) : NaN;
        const plugIdx = Number.isFinite(plugNum) ? plugNum - 1 : -1;
        const raw = plugIdx >= 0 ? row?.CPower?.[plugIdx] : undefined;
        const unknown = raw === null || raw === undefined || (typeof raw === 'number' && Number.isNaN(raw));
        const val = unknown ? '-' : `${Number(chosen.value).toFixed(1)} W`;
        const hideAction = Number.isFinite(plugNum)
          ? '<span style="display:block;margin-top:6px;color:#d1d5db;font-size:11px;">Click this line to hide this plug</span>'
          : '';
        return [
          `${timestamp}`,
          `<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${chosen.color};"></span>${chosen.seriesName}: ${val}`,
          hideAction,
        ].join('<br/>');
      },
    },
    legend: {
      show: false,
      data: legendNames,
      textStyle: { color: 'grey' },
      formatter: (name: string) => name.replace(/^Plug\s+/, ''),
      bottom: 0,
      padding: [5, 0, 0, 0],
      selected: legendSelected,
      selector: [
        { type: 'all', title: 'View All' },
        { type: 'inverse', title: 'All Off' },
      ],
      selectorPosition: 'end' as const,
      selectorLabel: { color: 'grey' },
    },
    grid: { left: '3%', right: '4%', bottom: '12%', containLabel: true },
    toolbox: { feature: { saveAsImage: {} } },
    xAxis: {
      type: 'category' as const,
      boundaryGap: true,
      data: xAxisData,
      axisLabel: {
        color: 'grey',
        rotate: 45,
        interval: (index: number) => {
          const row = displayData[index];
          const timestamp = row ? toEasternDateTime(row) : DateTime.now().setZone('America/New_York');
          return shouldShowTimeLabel(timestamp, startDateTime, interval, showHalfHours);
        },
        formatter: (value: string) => {
          const idx = xAxisData.indexOf(value);
          const row = displayData[idx];
          const timestamp = row ? toEasternDateTime(row) : DateTime.now().setZone('America/New_York');
          return formatTimeLabel(timestamp, showHalfHours);
        },
      },
    },
    yAxis: {
      type: 'value' as const,
      name: 'Watts',
      axisLabel: { color: 'grey' },
      min: 0,
      max: yAxisMax,
    },
    series: seriesData,
  };

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        minWidth: 0,
        minHeight: '300px',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <Select
              multiple
              value={selectedPlugNums.map((n) => String(n))}
              displayEmpty
              renderValue={(selected) => {
                const count = selected.length;
                if (count === 0) return 'No plugs selected';
                if (count === visiblePlugNums.length) return `All plugs (${count})`;
                return `${count} plugs selected`;
              }}
              onChange={(event) => {
                const values = event.target.value as string[];
                const selectedSet = new Set(values.map((v) => Number(v)));
                const next: Record<string, boolean> = {};
                for (const plugNum of visiblePlugNums) {
                  next[`Plug ${plugNum}`] = selectedSet.has(plugNum);
                }
                setLegendSelected(next);
              }}
              MenuProps={{ PaperProps: { style: { maxHeight: 360 } } }}
            >
              {visiblePlugNums.map((plugNum) => {
                const label = `Plug ${plugNum}`;
                const checked = legendSelected[label] !== false;
                return (
                  <MenuItem key={plugNum} value={String(plugNum)}>
                    <Checkbox checked={checked} size="small" />
                    <ListItemText primary={label} />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" variant="text" onClick={() => {
              const next: Record<string, boolean> = {};
              for (const plugNum of visiblePlugNums) next[`Plug ${plugNum}`] = true;
              setLegendSelected(next);
            }}>All</Button>
            <Button size="small" variant="text" onClick={() => {
              const next: Record<string, boolean> = {};
              for (const plugNum of visiblePlugNums) next[`Plug ${plugNum}`] = false;
              setLegendSelected(next);
            }}>None</Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
            {lastChargeOrder.length > 0
              ? `Last charge order: ${lastChargeOrder.join(', ')}`
              : 'Last charge order: no charge sessions in selected range'}
          </Typography>
        </Box>
      </Box>
      <div style={{ flex: '1 1 auto', minHeight: 0 }}>
        {containerReady && (
          <ReactECharts
            ref={chartRef}
            option={option}
            className="cancelSelectorName"
            style={{ height: '100%', width: '100%', minHeight: '300px' }}
            opts={{ renderer: 'canvas' }}
            onEvents={{
              mouseover: (e: { seriesName?: string }) => {
                if (e?.seriesName?.startsWith('Plug ')) hoveredSeriesNameRef.current = e.seriesName;
              },
              mouseout: () => { hoveredSeriesNameRef.current = null; },
              click: (e: { seriesName?: string }) => {
                const plugMatch = e?.seriesName?.match(/^Plug\s+(\d+)$/);
                if (!plugMatch) return;
                hidePlug(Number(plugMatch[1]));
              },
              legendselectchanged: (e: { selected?: Record<string, boolean> }) => {
                if (e?.selected) setLegendSelected(e.selected);
              },
              legendinverseselect: (e: { selected?: Record<string, boolean> }) => {
                if (e?.selected) setLegendSelected(e.selected);
              },
              legendselectall: (e: { selected?: Record<string, boolean> }) => {
                if (e?.selected) setLegendSelected(e.selected);
              },
            }}
            onChartReady={(chart) => {
              const resizeIfReady = () => {
                if (!containerRef.current) return;
                const { width, height } = containerRef.current.getBoundingClientRect();
                if (width > 0 && height > 0) chart.resize({ width: Math.floor(width), height: Math.floor(height) });
              };
              [0, 50, 100, 200, 400, 800, 1200].forEach((ms) => setTimeout(resizeIfReady, ms));
            }}
            notMerge={false}
          />
        )}
      </div>
    </div>
  );
};

export default PlugPowerGraph;
